import type { Layer } from "leaflet";
import type { ReactNode, RefObject } from "react";
import { useMemo, useRef } from "react";

import {
    type FixtureLayerEntry,
    FixtureSelectionContext,
    type FixtureSelectionContextType,
} from "./FixtureSelectionContext";

const add = (
    layersRef: RefObject<Record<string, FixtureLayerEntry>>,
    uniqueId: string,
    ...layers: Layer[]
) => {
    layersRef.current[uniqueId] = { layers };

    return () => {
        delete layersRef.current[uniqueId];
    };
};

/**
 * Context provider exposing get/add functions for the fixture layer registry.
 *
 * @param root0 - The component props.
 * @param root0.children - The child components to render.
 * @returns The rendered provider component.
 */
export const FixtureSelectionProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const layersRef = useRef<Record<string, FixtureLayerEntry>>({});

    const value = useMemo<FixtureSelectionContextType>(
        () => ({
            get: (uniqueId) => layersRef.current[uniqueId],
            add: (...args) => add(layersRef, ...args),
        }),
        [],
    );

    return (
        <FixtureSelectionContext value={value}>
            {children}
        </FixtureSelectionContext>
    );
};
